import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container, Typography, Box, Grid, TextField, Button, MenuItem, Paper, Divider, Alert, useTheme, useMediaQuery
} from '@mui/material';
import { isSuperAdminEmail } from '../config/adminEmails';
import { Sponsor } from '../types';

const LEVELS = [
  'Beginner',
  'Intermediate -',
  'Intermediate',
  'Intermediate +',
  'Advanced -',
  'Advanced',
  'Advanced +',
];

interface PlayerInput {
  name: string;
  level: string;
}

interface PairingResult {
  teamA: PlayerInput[];
  teamB: PlayerInput[];
  isGandongan: boolean;
}

interface MultiMatchResult {
  matches: PairingResult[][]; // Array of rounds, each round is array of PairingResult
  playerMatches: Record<string, number[]>; // player name -> array of match indices
  gandongCount: Record<string, number>; // advanced player -> berapa kali menggandong pemain level lebih rendah (Beginner/Intermediate)
  gandongDetails: Array<{ advanced: string; carried: string }>; // log siapa menggandong siapa
}

function getLevelIndex(level: string) {
  return LEVELS.indexOf(level);
}


function getTeamLevelSum(team: PlayerInput[]) {
  return team.reduce((sum, p) => sum + getLevelIndex(p.level), 0);
}

function countGandongan(team: PlayerInput[]) {
  return team[0].level !== team[1].level ? 1 : 0;
}

function allCombinations(arr: PlayerInput[]): PlayerInput[][] {
  // Semua kombinasi 2 dari 4
  return [
    [arr[0], arr[1]],
    [arr[0], arr[2]],
    [arr[0], arr[3]],
    [arr[1], arr[2]],
    [arr[1], arr[3]],
    [arr[2], arr[3]],
  ];
}

function isBeginner(level: string) { return getLevelIndex(level) === 0; }
function isIntermediate(level: string) { const idx = getLevelIndex(level); return idx >= 1 && idx <= 3; }
function isAdvanced(level: string) { return getLevelIndex(level) >= 4; }

function hasBeginner(team: PlayerInput[]) { return team.some(p => isBeginner(p.level)); }
function hasIntermediate(team: PlayerInput[]) { return team.some(p => isIntermediate(p.level)); }
function hasAdvanced(group: PlayerInput[]) { return group.some(p => isAdvanced(p.level)); }
function countAdvanced(team: PlayerInput[]) { return team.filter(p => isAdvanced(p.level)).length; }
function countBeginner(team: PlayerInput[]) { return team.filter(p => isBeginner(p.level)).length; }
function countIntermediate(team: PlayerInput[]) { return team.filter(p => isIntermediate(p.level)).length; }

// Advanced menggandong pemain level lebih rendah (Beginner/Intermediate)
function getAdvCarryPair(team: PlayerInput[]): { advanced: PlayerInput | null; carried: PlayerInput | null } {
  if (team.length !== 2) return { advanced: null, carried: null };
  const a = team[0];
  const b = team[1];
  // Jika keduanya advanced (misal Advanced dan Advanced -), tidak dihitung gandongan
  if (isAdvanced(a.level) && isAdvanced(b.level)) return { advanced: null, carried: null };
  if (isAdvanced(a.level) && (isBeginner(b.level) || isIntermediate(b.level))) return { advanced: a, carried: b };
  if (isAdvanced(b.level) && (isBeginner(a.level) || isIntermediate(a.level))) return { advanced: b, carried: a };
  return { advanced: null, carried: null };
}

function pairPenalty(teamA: PlayerInput[], teamB: PlayerInput[], group: PlayerInput[], gandongCount?: Record<string, number>, partnerHistory?: Record<string, Set<string>>, opponentHistory?: Record<string, Set<string>>): number {
  let penalty = 0;
  
  // ATURAN SANGAT KETAT: LARANG KERAS Advanced/Advanced-+Beginner dalam satu tim
  // Termasuk: Advanced+Beginner, Advanced-+Beginner, Advanced++Beginner
  const teamABeginnerWithAdvanced = hasBeginner(teamA) && hasAdvanced(teamA);
  const teamBBeginnerWithAdvanced = hasBeginner(teamB) && hasAdvanced(teamB);
  
  // Hitung jumlah pemain per level di grup
  const groupBeginnerCount = group.filter(p => isBeginner(p.level)).length;
  const groupIntermediateCount = group.filter(p => isIntermediate(p.level)).length;
  const groupAdvancedCount = group.filter(p => isAdvanced(p.level)).length;
  
  // ATURAN BARU: Jika ada Intermediate yang tersedia, LARANG KERAS Advanced+Beginner
  const hasIntermediateAvailable = groupIntermediateCount >= 1; // Minimal ada 1 Intermediate
  
  // LARANG KERAS: Advanced+Beginner dalam satu tim - Penalti EKSTREM MUTLAK
  // Cek apakah masih ada Advanced lain di grup yang bisa dipasangkan
  const remainingAdvanced = group.filter(p => 
    !teamA.includes(p) && !teamB.includes(p) && isAdvanced(p.level)
  );
  
  if (teamABeginnerWithAdvanced) {
    // Jika masih ada Advanced lain yang tersedia, LARANG MUTLAK
    if (remainingAdvanced.length > 0 || groupAdvancedCount >= 2) {
      penalty += 500000; // Penalti EKSTREM MUTLAK jika masih ada Advanced yang tersedia
    } else if (hasIntermediateAvailable) {
      penalty += 300000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      // Hanya diperbolehkan jika benar-benar tidak ada alternatif sama sekali
      penalty += 200000; // Penalti sangat tinggi meskipun tidak ada alternatif
    }
  }
  if (teamBBeginnerWithAdvanced) {
    // Jika masih ada Advanced lain yang tersedia, LARANG MUTLAK
    if (remainingAdvanced.length > 0 || groupAdvancedCount >= 2) {
      penalty += 500000; // Penalti EKSTREM MUTLAK jika masih ada Advanced yang tersedia
    } else if (hasIntermediateAvailable) {
      penalty += 300000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      // Hanya diperbolehkan jika benar-benar tidak ada alternatif sama sekali
      penalty += 200000; // Penalti sangat tinggi meskipun tidak ada alternatif
    }
  }

  // ATURAN SANGAT KETAT: LARANG KERAS Beginner vs Advanced/Advanced- (sebagai lawan)
  // Termasuk: Beginner vs Advanced, Beginner vs Advanced-, Beginner vs Advanced+
  const groupHasIntermediate = hasIntermediate(group);
  const aBeginner = hasBeginner(teamA); const bBeginner = hasBeginner(teamB);
  const aAdvanced = hasAdvanced(teamA); const bAdvanced = hasAdvanced(teamB);
  // totalBeginnerInMatch removed - not used
  
  // ATURAN BARU: Penalti lebih tinggi untuk Beginner vs Advanced jika ada alternatif
  
  // LARANG KERAS: Beginner vs Advanced (sebagai lawan) - Penalti EKSTREM MUTLAK
  // Cek apakah masih ada Advanced lain di grup yang bisa dipasangkan
  const remainingAdvancedForOpponent = group.filter(p => 
    !teamA.includes(p) && !teamB.includes(p) && isAdvanced(p.level)
  );
  
  if (aBeginner && bAdvanced) {
    // Jika masih ada Advanced lain yang tersedia, LARANG MUTLAK
    if (remainingAdvancedForOpponent.length > 0 || groupAdvancedCount >= 2) {
      penalty += 400000; // Penalti EKSTREM MUTLAK jika masih ada Advanced yang tersedia
    } else if (hasIntermediateAvailable) {
      penalty += 250000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      // Hanya diperbolehkan jika benar-benar tidak ada alternatif sama sekali
      penalty += 150000; // Penalti sangat tinggi meskipun tidak ada alternatif
    }
  }
  if (bBeginner && aAdvanced) {
    // Jika masih ada Advanced lain yang tersedia, LARANG MUTLAK
    if (remainingAdvancedForOpponent.length > 0 || groupAdvancedCount >= 2) {
      penalty += 400000; // Penalti EKSTREM MUTLAK jika masih ada Advanced yang tersedia
    } else if (hasIntermediateAvailable) {
      penalty += 250000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      // Hanya diperbolehkan jika benar-benar tidak ada alternatif sama sekali
      penalty += 150000; // Penalti sangat tinggi meskipun tidak ada alternatif
    }
  }
  
  // ATURAN TAMBAHAN: Larang keras jika ada Beginner di tim A dan Advanced di tim B (atau sebaliknya)
  // bahkan jika mereka tidak langsung berhadapan, tetap beri penalti karena ketidakseimbangan
  if ((aBeginner && bAdvanced) || (bBeginner && aAdvanced)) {
    // Cek apakah ada Intermediate di grup yang bisa digunakan untuk menghindari pairing ini
    const remainingIntermediate = group.filter(p => 
      !teamA.includes(p) && !teamB.includes(p) && isIntermediate(p.level)
    );
    if (remainingIntermediate.length > 0) {
      penalty += 100000; // Penalti tambahan jika masih ada Intermediate yang tersedia
    }
  }
  
  // ATURAN PRIORITAS MUTLAK: Jika ada Advanced di grup, mereka HARUS bermain dengan Advanced terlebih dahulu
  // Cek apakah ada Advanced di tim yang tidak bermain dengan Advanced padahal masih ada Advanced lain
  const advancedInTeamA = teamA.filter(p => isAdvanced(p.level));
  const advancedInTeamB = teamB.filter(p => isAdvanced(p.level));
  const totalAdvancedInMatch = advancedInTeamA.length + advancedInTeamB.length;
  const remainingAdvancedInGroup = group.filter(p => 
    !teamA.includes(p) && !teamB.includes(p) && isAdvanced(p.level)
  );
  
  // Jika ada Advanced di match tapi tidak semua Advanced bermain dengan Advanced
  if (totalAdvancedInMatch > 0) {
    // Cek apakah Advanced bermain dengan Advanced (dalam tim atau sebagai lawan)
    const advancedPlaysWithAdvanced = 
      (advancedInTeamA.length === 2) || // 2 Advanced dalam satu tim
      (advancedInTeamB.length === 2) || // 2 Advanced dalam satu tim
      (advancedInTeamA.length === 1 && advancedInTeamB.length === 1); // 1 Advanced vs 1 Advanced
    
    // Jika Advanced tidak bermain dengan Advanced padahal masih ada Advanced lain
    if (!advancedPlaysWithAdvanced && (remainingAdvancedInGroup.length > 0 || groupAdvancedCount > totalAdvancedInMatch)) {
      // Beri penalti EKSTREM untuk memaksa Advanced bermain dengan Advanced
      penalty += 600000; // Penalti EKSTREM MUTLAK
    }
    
    // Jika Advanced bermain dengan Beginner/Intermediate padahal masih ada Advanced lain
    const advancedWithBeginner = (advancedInTeamA.length > 0 && hasBeginner(teamA)) || 
                                 (advancedInTeamB.length > 0 && hasBeginner(teamB));
    const advancedWithIntermediate = (advancedInTeamA.length > 0 && hasIntermediate(teamA) && !hasAdvanced(teamA)) ||
                                     (advancedInTeamB.length > 0 && hasIntermediate(teamB) && !hasAdvanced(teamB));
    
    if ((advancedWithBeginner || advancedWithIntermediate) && remainingAdvancedInGroup.length > 0) {
      penalty += 700000; // Penalti EKSTREM MUTLAK untuk memaksa Advanced bermain dengan Advanced
    }
  }

  // SISTEM PRIORITAS BERDASARKAN URUTAN LEVEL
  // Fungsi untuk mendapatkan level tertinggi dan terendah dalam tim
  const getTeamLevels = (team: PlayerInput[]) => {
    return team.map(p => p.level).sort((a, b) => getLevelIndex(b) - getLevelIndex(a));
  };
  
  const teamALevels = getTeamLevels(teamA);
  const teamBLevels = getTeamLevels(teamB);
  
  // Fungsi untuk menghitung bonus berdasarkan urutan prioritas
  const getPriorityBonus = (level1: string, level2: string): number => {
    // Urutan prioritas (semakin besar bonus, semakin tinggi prioritas):
    // 1. Advanced + Advanced
    if ((level1 === 'Advanced' || level1 === 'Advanced +') && (level2 === 'Advanced' || level2 === 'Advanced +')) return 50000;
    // 2. Advanced + Advanced - (dikurangi karena ini kurang seimbang, perlu dengan Intermediate+)
    if (((level1 === 'Advanced' || level1 === 'Advanced +') && level2 === 'Advanced -') || 
        (level1 === 'Advanced -' && (level2 === 'Advanced' || level2 === 'Advanced +'))) return 30000;
    // 3. Advanced - + Advanced -
    if (level1 === 'Advanced -' && level2 === 'Advanced -') return 40000;
    // 4. Advanced + Intermediate + (ditingkatkan karena ini kombinasi seimbang)
    if (((level1 === 'Advanced' || level1 === 'Advanced +') && level2 === 'Intermediate +') || 
        (level1 === 'Intermediate +' && (level2 === 'Advanced' || level2 === 'Advanced +'))) return 42000;
    // 5. Advanced - + Intermediate +
    if ((level1 === 'Advanced -' && level2 === 'Intermediate +') || 
        (level1 === 'Intermediate +' && level2 === 'Advanced -')) return 38000;
    // 6. Intermediate + + Intermediate +
    if (level1 === 'Intermediate +' && level2 === 'Intermediate +') return 30000;
    // 7. Intermediate + + Intermediate
    if ((level1 === 'Intermediate +' && level2 === 'Intermediate') || 
        (level1 === 'Intermediate' && level2 === 'Intermediate +')) return 25000;
    // 7. Intermediate + Intermediate
    if (level1 === 'Intermediate' && level2 === 'Intermediate') return 20000;
    // 8. Intermediate + Intermediate -
    if ((level1 === 'Intermediate' && level2 === 'Intermediate -') || 
        (level1 === 'Intermediate -' && level2 === 'Intermediate')) return 15000;
    // 9. Intermediate - + Intermediate -
    if (level1 === 'Intermediate -' && level2 === 'Intermediate -') return 10000;
    // 10. Intermediate - + Beginner
    if ((level1 === 'Intermediate -' && level2 === 'Beginner') || 
        (level1 === 'Beginner' && level2 === 'Intermediate -')) return 5000;
    
    return 0;
  };
  
  // Hitung bonus untuk kombinasi dalam tim (partner)
  const teamAPartnerBonus = getPriorityBonus(teamALevels[0], teamALevels[1]);
  const teamBPartnerBonus = getPriorityBonus(teamBLevels[0], teamBLevels[1]);
  
  // Hitung bonus untuk kombinasi antar tim (lawan)
  // Ambil level tertinggi dari masing-masing tim
  const teamAHighest = teamALevels[0];
  const teamBHighest = teamBLevels[0];
  const teamALowest = teamALevels[1];
  const teamBLowest = teamBLevels[1];
  
  // Bonus untuk lawan: cek kombinasi level tertinggi vs tertinggi, dan terendah vs terendah
  const opponentBonusHigh = getPriorityBonus(teamAHighest, teamBHighest);
  const opponentBonusLow = getPriorityBonus(teamALowest, teamBLowest);
  
  // Total bonus: partner bonus + opponent bonus (rata-rata)
  const totalBonus = teamAPartnerBonus + teamBPartnerBonus + (opponentBonusHigh + opponentBonusLow) / 2;
  penalty -= totalBonus;
  
  // BONUS untuk Intermediate+Advanced dalam satu tim (lebih baik daripada Advanced+Beginner)
  const teamAIntermediateWithAdvanced = hasIntermediate(teamA) && hasAdvanced(teamA) && !hasBeginner(teamA);
  const teamBIntermediateWithAdvanced = hasIntermediate(teamB) && hasAdvanced(teamB) && !hasBeginner(teamB);
  // Beri bonus untuk kombinasi Intermediate+Advanced karena ini lebih seimbang
  if (teamAIntermediateWithAdvanced) penalty -= 5000; // Bonus besar untuk kombinasi ini
  if (teamBIntermediateWithAdvanced) penalty -= 5000; // Bonus besar untuk kombinasi ini

  // PRIORITAS: Bonus besar untuk Beginner+Beginner pairing
  const teamABeginnerBeginner = countBeginner(teamA) === 2;
  const teamBBeginnerBeginner = countBeginner(teamB) === 2;
  if (teamABeginnerBeginner) {
    penalty -= 20000; // Bonus sangat besar untuk Beginner+Beginner
  }
  if (teamBBeginnerBeginner) {
    penalty -= 20000; // Bonus sangat besar untuk Beginner+Beginner
  }
  // Bonus ekstra jika kedua tim adalah Beginner+Beginner
  if (teamABeginnerBeginner && teamBBeginnerBeginner) {
    penalty -= 10000; // Bonus tambahan untuk kombinasi ideal
  }

  // PRIORITAS: Bonus untuk Beginner+Intermediate- pairing (jika tidak ada Beginner lain)
  const hasIntermediateMinus = (team: PlayerInput[]) => team.some(p => p.level === 'Intermediate -');
  const teamABeginnerWithIntMinus = hasBeginner(teamA) && hasIntermediateMinus(teamA) && !hasAdvanced(teamA);
  const teamBBeginnerWithIntMinus = hasBeginner(teamB) && hasIntermediateMinus(teamB) && !hasAdvanced(teamB);
  
  // Cek apakah masih ada Beginner lain di grup yang belum dipasangkan
  const remainingBeginner = group.filter(p => 
    !teamA.includes(p) && !teamB.includes(p) && isBeginner(p.level)
  );
  
  // Hitung jumlah Beginner yang sudah dipasangkan
  // Used in conditional logic for pairing decisions
  
  // Jika jumlah Beginner di grup <= total Beginner yang sudah dipasangkan (tidak ada Beginner lain),
  // atau jika hanya ada 1 Beginner di grup, beri bonus untuk Beginner+Intermediate- pairing
  if (teamABeginnerWithIntMinus && (remainingBeginner.length === 0 || groupBeginnerCount === 1)) {
    penalty -= 15000; // Bonus besar untuk Beginner+Intermediate- jika tidak ada Beginner lain
  }
  if (teamBBeginnerWithIntMinus && (remainingBeginner.length === 0 || groupBeginnerCount === 1)) {
    penalty -= 15000; // Bonus besar untuk Beginner+Intermediate- jika tidak ada Beginner lain
  }

  // SISTEM BONUS: Utamakan pemain bermain sesama level
  // Bonus untuk kombinasi yang ideal (sesama level)
  const isSameLevel = (team: PlayerInput[]) => {
    if (team.length !== 2) return false;
    return team[0].level === team[1].level;
  };
  
  const teamASameLevel = isSameLevel(teamA);
  const teamBSameLevel = isSameLevel(teamB);
  
  // Bonus besar untuk kedua tim sesama level
  // HANYA jika level kedua tim juga sama (misal: Intermediate/Intermediate vs Intermediate/Intermediate)
  if (teamASameLevel && teamBSameLevel) {
    // Pastikan level kedua tim sama (bukan hanya level dalam tim yang sama)
    if (teamA[0].level === teamB[0].level) {
      penalty -= 5000; // Bonus besar untuk kombinasi ideal (4 pemain level sama)
    } else {
      // Kasus seperti Intermediate/Intermediate vs Intermediate+/Intermediate+
      // Ini tidak seimbang, jangan beri bonus (penalti akan ditangani di bagian distribusi level)
      // Tidak perlu menambah penalti di sini karena sudah ditangani di bagian distribusi level
    }
  } else if (teamASameLevel || teamBSameLevel) {
    penalty -= 2000; // Bonus sedang untuk salah satu tim sesama level
  }
  
  // Bonus untuk kombinasi level yang berdekatan (Intermediate dengan Intermediate)
  const isAdjacentLevel = (team: PlayerInput[]) => {
    if (team.length !== 2) return false;
    const level1 = getLevelIndex(team[0].level);
    const level2 = getLevelIndex(team[1].level);
    return Math.abs(level1 - level2) <= 1;
  };
  
  const teamAAdjacent = isAdjacentLevel(teamA);
  const teamBAdjacent = isAdjacentLevel(teamB);
  
  if (teamAAdjacent && teamBAdjacent && !teamASameLevel && !teamBSameLevel) {
    penalty -= 1000; // Bonus kecil untuk level berdekatan
  }

  // SISTEM KESEIMBANGAN ANTAR TIM: Prioritaskan kombinasi yang seimbang
  const getTeamLevelBalance = (team: PlayerInput[]) => {
    if (team.length !== 2) return 0;
    const level1 = getLevelIndex(team[0].level);
    const level2 = getLevelIndex(team[1].level);
    return level1 + level2; // Total level tim
  };

  const teamABalance = getTeamLevelBalance(teamA);
  const teamBBalance = getTeamLevelBalance(teamB);
  const balanceDifference = Math.abs(teamABalance - teamBBalance);

  // ATURAN KHUSUS: Distribusi level yang seimbang antar tim
  // Hindari kombinasi seperti: Intermediate/Intermediate vs Intermediate+/Intermediate+
  // Prioritaskan: Intermediate+/Intermediate vs Intermediate+/Intermediate
  const getTeamLevelComposition = (team: PlayerInput[]) => {
    if (team.length !== 2) return null;
    const levels = [team[0].level, team[1].level].sort((a, b) => getLevelIndex(b) - getLevelIndex(a)); // Urutkan dari tinggi ke rendah
    return { high: levels[0], low: levels[1] };
  };

  const compA = getTeamLevelComposition(teamA);
  const compB = getTeamLevelComposition(teamB);
  
  // Cek apakah kedua tim memiliki level yang sama (misal: Intermediate/Intermediate vs Intermediate+/Intermediate+)
  if (compA && compB) {
    const teamASameLevel = compA.high === compA.low;
    const teamBSameLevel = compB.high === compB.low;
    
    // Jika kedua tim memiliki level yang sama tetapi berbeda level antar tim, ini tidak seimbang
    if (teamASameLevel && teamBSameLevel && compA.high !== compB.high) {
      // Contoh: Intermediate/Intermediate vs Intermediate+/Intermediate+
      // Ini tidak seimbang, beri penalti tinggi
      const levelDiff = Math.abs(getLevelIndex(compA.high) - getLevelIndex(compB.high));
      if (levelDiff === 1) {
        // Kasus seperti Intermediate/Intermediate vs Intermediate+/Intermediate+
        penalty += 10000; // Penalti sangat tinggi untuk ketidakseimbangan ini
      } else if (levelDiff >= 2) {
        penalty += 15000; // Penalti lebih tinggi untuk perbedaan level lebih besar
      }
    }
    
    // Bonus besar untuk kombinasi yang seimbang: high/low vs high/low (dengan level yang sama)
    // Contoh: Intermediate+/Intermediate vs Intermediate+/Intermediate
    if (!teamASameLevel && !teamBSameLevel) {
      // Kedua tim memiliki kombinasi high/low
      if (compA.high === compB.high && compA.low === compB.low) {
        // Kombinasi yang sempurna: level tinggi dan rendah sama di kedua tim
        penalty -= 8000; // Bonus sangat besar untuk kombinasi seimbang sempurna
      } else if (getLevelIndex(compA.high) === getLevelIndex(compB.high) && 
                 Math.abs(getLevelIndex(compA.low) - getLevelIndex(compB.low)) <= 1) {
        // Level tinggi sama, level rendah berdekatan (contoh: Intermediate+/Intermediate vs Intermediate+/Intermediate-)
        penalty -= 5000; // Bonus besar untuk kombinasi yang sangat seimbang
      } else if (Math.abs(getLevelIndex(compA.high) - getLevelIndex(compB.high)) <= 1 &&
                 Math.abs(getLevelIndex(compA.low) - getLevelIndex(compB.low)) <= 1) {
        // Kedua level berdekatan (contoh: Intermediate+/Intermediate vs Intermediate+/Intermediate-)
        penalty -= 3000; // Bonus untuk kombinasi yang seimbang
      }
    }
  }

  // Bonus besar untuk keseimbangan yang sempurna (selisih 0)
  if (balanceDifference === 0) {
    penalty -= 3000; // Bonus besar untuk keseimbangan sempurna
  } else if (balanceDifference === 1) {
    penalty -= 1500; // Bonus sedang untuk keseimbangan sangat baik
  } else if (balanceDifference === 2) {
    penalty -= 500; // Bonus kecil untuk keseimbangan baik
  } else if (balanceDifference === 3) {
    penalty += 5000; // Penalti tinggi untuk ketidakseimbangan besar (misal: 2 Intermediate vs 2 Beginner)
  } else if (balanceDifference >= 4) {
    penalty += 2000; // Penalti untuk ketidakseimbangan besar
  }

  // Penalti besar untuk ketidakseimbangan level ekstrem
  // Kasus khusus: Advanced/Intermediate+ vs Beginner/Beginner memiliki balanceDifference >= 5
  if (balanceDifference >= 6) {
    // Kasus ekstrem seperti Advanced/Intermediate+ vs Beginner/Beginner
    if (hasIntermediateAvailable || groupIntermediateCount >= 1) {
      penalty += 80000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      penalty += 50000; // Penalti sangat tinggi
    }
  } else if (balanceDifference >= 5) {
    if (hasIntermediateAvailable || groupIntermediateCount >= 1) {
      penalty += 60000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      penalty += 30000; // Penalti sangat tinggi
    }
  } else if (balanceDifference >= 4) {
    penalty += 10000;
  }

  // Penalti sangat tinggi: (Intermediate/Intermediate+) + Beginner vs Intermediate+/Advanced(-/+) (dan sebaliknya)
  const teamAIntOrPlusAndBegin = (teamA.some(p => p.level === 'Intermediate' || p.level === 'Intermediate +') && teamA.some(p => isBeginner(p.level)));
  const teamBIntOrPlusAndBegin = (teamB.some(p => p.level === 'Intermediate' || p.level === 'Intermediate +') && teamB.some(p => isBeginner(p.level)));
  const teamAIntPlusOrAdv = teamA.some(p => p.level === 'Intermediate +' || isAdvanced(p.level));
  const teamBIntPlusOrAdv = teamB.some(p => p.level === 'Intermediate +' || isAdvanced(p.level));
  // (tim A Intermediate/Int++Beginner, tim B Intermediate+/Advanced apapun) atau sebaliknya
  if ((teamAIntOrPlusAndBegin && teamBIntPlusOrAdv) || (teamBIntOrPlusAndBegin && teamAIntPlusOrAdv)) {
    penalty += 20000;
  }

  // ATURAN KHUSUS: Prioritaskan kombinasi Advanced/Advanced- vs Advanced/Advanced-
  const hasAdvancedAndAdvancedMinus = (team: PlayerInput[]) => {
    const levels = team.map(p => p.level);
    return levels.includes('Advanced') && levels.includes('Advanced -');
  };

  const teamAAdvAdvMinus = hasAdvancedAndAdvancedMinus(teamA);
  const teamBAdvAdvMinus = hasAdvancedAndAdvancedMinus(teamB);

  if (teamAAdvAdvMinus && teamBAdvAdvMinus) {
    penalty -= 2000; // Bonus besar untuk kombinasi seimbang ini
  }

  // ATURAN KHUSUS: Hindari Advanced/Advanced vs Advanced-/Advanced- (tidak seimbang)
  const hasTwoAdvanced = (team: PlayerInput[]) => {
    const levels = team.map(p => p.level);
    return levels.filter(l => l === 'Advanced').length === 2;
  };

  const hasTwoAdvancedMinus = (team: PlayerInput[]) => {
    const levels = team.map(p => p.level);
    return levels.filter(l => l === 'Advanced -').length === 2;
  };

  const teamATwoAdv = hasTwoAdvanced(teamA);
  const teamBTwoAdv = hasTwoAdvanced(teamB);
  const teamATwoAdvMinus = hasTwoAdvancedMinus(teamA);
  const teamBTwoAdvMinus = hasTwoAdvancedMinus(teamB);

  if ((teamATwoAdv && teamBTwoAdvMinus) || (teamBTwoAdv && teamATwoAdvMinus)) {
    penalty += 8000; // Penalti tinggi untuk ketidakseimbangan Advanced vs Advanced-
  }

  // ATURAN KHUSUS: LARANG KERAS Advanced/Advanced vs Advanced-/Intermediate+
  // Contoh: Zulvi (Advanced)/Coki (Advanced) vs @yah kembar (Advanced-)/Trivena (Intermediate+)
  // Ini sangat tidak seimbang (9 vs 7). Lebih baik Advanced/Intermediate+ vs Advanced-/Advanced-
  
  // Local helper functions for this section
  const hasAdvancedMinus = (team: PlayerInput[]) => team.some(p => p.level === 'Advanced -');
  const hasIntermediatePlus = (team: PlayerInput[]) => team.some(p => p.level === 'Intermediate +');
  
  const teamAHasAdv = hasAdvanced(teamA);
  const teamAHasAdvMinus = hasAdvancedMinus(teamA);
  const teamAHasIntPlus = hasIntermediatePlus(teamA);
  const teamBHasAdv = hasAdvanced(teamB);
  const teamBHasAdvMinus = hasAdvancedMinus(teamB);
  const teamBHasIntPlus = hasIntermediatePlus(teamB);
  
  // Larang keras: Advanced/Advanced vs Advanced-/Intermediate+
  if ((teamATwoAdv && teamBHasAdvMinus && teamBHasIntPlus) || 
      (teamBTwoAdv && teamAHasAdvMinus && teamAHasIntPlus)) {
    // Cek apakah ada alternatif yang lebih seimbang di grup
    const remainingAdvancedMinusCount = group.filter(p => p.level === 'Advanced -').length - 
                                        (teamAHasAdvMinus ? 1 : 0) - 
                                        (teamBHasAdvMinus ? 1 : 0);
    if (remainingAdvancedMinusCount > 0 || 
        group.filter(p => isAdvanced(p.level)).length > 2) {
      penalty += 25000; // Penalti SANGAT tinggi untuk kombinasi yang sangat tidak seimbang ini
    }
  }

  // ATURAN KHUSUS: LARANG KERAS Advanced/Advanced- vs Advanced-/Intermediate+
  // Contoh: Agung (Advanced)/Zulvi (Advanced-) vs David AC (Advanced-)/Trivena (Intermediate+)
  // Ini tidak seimbang (9 vs 7). Lebih baik Advanced/Intermediate+ vs Advanced-/Advanced- (8 vs 8)
  const hasAdvancedNotMinus = (team: PlayerInput[]) => team.some(p => p.level === 'Advanced' || p.level === 'Advanced +');
  
  const teamAHasAdvNotMinus = hasAdvancedNotMinus(teamA);
  const teamBHasAdvNotMinus = hasAdvancedNotMinus(teamB);
  
  // Check: Team A memiliki Advanced (bukan -) + Advanced-, Team B memiliki Advanced- + Intermediate+
  const teamAAdvAndAdvMinus = teamAHasAdvNotMinus && teamAHasAdvMinus;
  const teamBAdvAndAdvMinus = teamBHasAdvNotMinus && teamBHasAdvMinus;
  
  if ((teamAAdvAndAdvMinus && teamBHasAdvMinus && teamBHasIntPlus) || 
      (teamBAdvAndAdvMinus && teamAHasAdvMinus && teamAHasIntPlus)) {
    // Cek apakah ada Advanced- lain dan Intermediate+ di grup
    const advMinusCount = group.filter(p => p.level === 'Advanced -').length;
    const intPlusCount = group.filter(p => p.level === 'Intermediate +').length;
    // MUTLAK LARANG: Jika ada 2+ Advanced- dan 1+ Intermediate+, ini pasti ada alternatif yang lebih seimbang
    if (advMinusCount >= 2 && intPlusCount >= 1) {
      penalty += 30000; // Penalti EKSTREM untuk kombinasi yang dapat diubah menjadi sempurna
    }
  }

  // Jika grup mengandung Beginner dan Advanced sekaligus, beri penalti baseline untuk dorong pemisahan
  const groupHasBeginner = hasBeginner(group);
  const groupHasAdvanced = hasAdvanced(group);
  if (groupHasBeginner && groupHasAdvanced) penalty += 3000;

  // Jika ada >=2 Beginner di grup, dorong Beginner berpasangan dengan Beginner/Intermediate, bukan Advanced
  if (groupBeginnerCount >= 2) {
    if (teamABeginnerWithAdvanced) penalty += 1500;
    if (teamBBeginnerWithAdvanced) penalty += 1500;
    if (aBeginner && bAdvanced) penalty += 800;
    if (bBeginner && aAdvanced) penalty += 800;
    
    // ATURAN BARU: Jika ada >=2 Beginner, larang keras mereka melawan Advanced/Advanced
    const teamAHasTwoBeg = countBeginner(teamA) === 2;
    const teamBHasTwoBeg = countBeginner(teamB) === 2;
    const teamAHasTwoAdv = countAdvanced(teamA) === 2;
    const teamBHasTwoAdv = countAdvanced(teamB) === 2;
    
    if ((teamAHasTwoBeg && teamBHasTwoAdv) || (teamBHasTwoBeg && teamAHasTwoAdv)) {
      penalty += 60000; // Penalti ekstrem untuk kasus ini
    }
  }

  // ATURAN KHUSUS: Larang keras kombinasi yang sangat tidak seimbang
  // Contoh: Intermediate-/Advanced vs Beginner/Beginner
  // Menggunakan hasIntermediateMinus yang sudah dideklarasikan sebelumnya
  const hasAdvancedLevel = (team: PlayerInput[]) => team.some(p => p.level.startsWith('Advanced'));
  const hasTwoBeginner = (team: PlayerInput[]) => team.filter(p => p.level === 'Beginner').length === 2;
  
  const teamAIntMinusAdv = hasIntermediateMinus(teamA) && hasAdvancedLevel(teamA);
  const teamBIntMinusAdv = hasIntermediateMinus(teamB) && hasAdvancedLevel(teamB);
  const teamATwoBeg = hasTwoBeginner(teamA);
  const teamBTwoBeg = hasTwoBeginner(teamB);
  
  // ATURAN BARU: Larang keras Beginner/Beginner vs Advanced/Advanced
  if ((teamATwoBeg && teamBTwoAdv) || (teamBTwoBeg && teamATwoAdv)) {
    penalty += 50000; // Penalti ekstrem tinggi untuk kombinasi ini
  }
  
  // ATURAN BARU: Larang keras satu Beginner dengan Advanced lawan Advanced/Advanced
  const teamAOneBegOneAdv = countBeginner(teamA) === 1 && countAdvanced(teamA) === 1;
  const teamBOneBegOneAdv = countBeginner(teamB) === 1 && countAdvanced(teamB) === 1;
  if ((teamAOneBegOneAdv && teamBTwoAdv) || (teamBOneBegOneAdv && teamATwoAdv)) {
    penalty += 40000; // Penalti sangat tinggi
  }
  
  // ATURAN BARU: Larang keras Beginner dengan Intermediate lawan Advanced/Advanced
  const teamABegInt = countBeginner(teamA) === 1 && countIntermediate(teamA) === 1 && countAdvanced(teamA) === 0;
  const teamBBegInt = countBeginner(teamB) === 1 && countIntermediate(teamB) === 1 && countAdvanced(teamB) === 0;
  if ((teamABegInt && teamBTwoAdv) || (teamBBegInt && teamATwoAdv)) {
    penalty += 35000; // Penalti sangat tinggi
  }
  
  // Larang keras: Int-/Adv vs Beg/Beg
  if ((teamAIntMinusAdv && teamBTwoBeg) || (teamBIntMinusAdv && teamATwoBeg)) {
    penalty += 20000; // Penalti sangat tinggi untuk kombinasi ini
  }
  
  // Larang keras: Adv vs Beg/Beg (jika ada Intermediate di grup)
  if (groupHasIntermediate) {
    if ((aAdvanced && teamBTwoBeg) || (bAdvanced && teamATwoBeg)) {
      penalty += 15000; // Penalti sangat tinggi
    }
  }

  // ATURAN KHUSUS: Larang keras Advanced/Intermediate+ vs Beginner/Beginner
  // Kasus: Advanced - / Intermediate + vs Beginner / Beginner
  const hasAdvancedOrIntermediatePlus = (team: PlayerInput[]) => {
    return team.some(p => isAdvanced(p.level) || p.level === 'Intermediate +');
  };
  
  const teamAHasAdvOrIntPlus = hasAdvancedOrIntermediatePlus(teamA);
  const teamBHasAdvOrIntPlus = hasAdvancedOrIntermediatePlus(teamB);
  
  // Penalti EKSTREM untuk Advanced/Intermediate+ vs Beginner/Beginner
  // Menggunakan teamATwoBeg dan teamBTwoBeg yang sudah dideklarasikan sebelumnya
  if ((teamAHasAdvOrIntPlus && teamBTwoBeg) || (teamBHasAdvOrIntPlus && teamATwoBeg)) {
    // Cek apakah ada Intermediate lain yang tersedia di grup
    const remainingIntermediate = group.filter(p => 
      !teamA.includes(p) && !teamB.includes(p) && isIntermediate(p.level)
    );
    if (remainingIntermediate.length > 0 || hasIntermediateAvailable) {
      penalty += 100000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      penalty += 60000; // Penalti sangat tinggi meskipun tidak ada Intermediate
    }
  }
  
  // ATURAN KHUSUS: Larang keras kombinasi Advanced/Intermediate+ vs Beginner/Beginner
  // Cek komposisi tim: jika satu tim punya Advanced/Intermediate+ dan tim lain punya 2 Beginner
  const teamAHasAdvAndIntPlus = (teamA.some(p => isAdvanced(p.level)) && teamA.some(p => p.level === 'Intermediate +')) ||
                                 (teamA.some(p => isAdvanced(p.level)) && teamA.some(p => isIntermediate(p.level) && !isBeginner(p.level)));
  const teamBHasAdvAndIntPlus = (teamB.some(p => isAdvanced(p.level)) && teamB.some(p => p.level === 'Intermediate +')) ||
                                 (teamB.some(p => isAdvanced(p.level)) && teamB.some(p => isIntermediate(p.level) && !isBeginner(p.level)));
  
  if ((teamAHasAdvAndIntPlus && teamBTwoBeg) || (teamBHasAdvAndIntPlus && teamATwoBeg)) {
    if (hasIntermediateAvailable || groupIntermediateCount >= 2) {
      penalty += 100000; // Penalti EKSTREM jika masih ada Intermediate yang tersedia
    } else {
      penalty += 70000; // Penalti sangat tinggi
    }
  }

  // Preferensi KOMPOSISI: jika grup hanya Beginner/Intermediate,
  // utamakan Beg/Beg vs Beg/Beg atau Beg/Int vs Beg/Int, dan hindari Beg/Beg vs Int/Int.
  const aBegCnt = countBeginner(teamA);
  const bBegCnt = countBeginner(teamB);
  const aIntCnt = countIntermediate(teamA);
  const bIntCnt = countIntermediate(teamB);
  if (!groupHasAdvanced) {
    const bothBegBeg = aBegCnt === 2 && bBegCnt === 2;
    const bothBegInt = aBegCnt === 1 && aIntCnt === 1 && bBegCnt === 1 && bIntCnt === 1;
    const begVsIntInt = (aBegCnt === 2 && bIntCnt === 2) || (bBegCnt === 2 && aIntCnt === 2);

    if (bothBegBeg || bothBegInt) {
      penalty -= 1000;
    }
    if (begVsIntInt) {
      penalty += 10000; // Penalti tinggi untuk 2 Intermediate vs 2 Beginner
    }
  }

  // PERATURAN KHUSUS: Larang Adv/Adv vs Beg/Beg sebisa mungkin
  const aAdvCnt = countAdvanced(teamA);
  const bAdvCnt = countAdvanced(teamB);
  const aBegCnt2 = aBegCnt; const bBegCnt2 = bBegCnt;
  
  // ATURAN BARU: Penalti ekstra untuk perbedaan level yang sangat besar
  const levelDifferencePenalty = (team1: PlayerInput[], team2: PlayerInput[]) => {
    const team1AvgLevel = team1.reduce((sum, p) => sum + getLevelIndex(p.level), 0) / team1.length;
    const team2AvgLevel = team2.reduce((sum, p) => sum + getLevelIndex(p.level), 0) / team2.length;
    const diff = Math.abs(team1AvgLevel - team2AvgLevel);
    
    // Jika perbedaan level > 2 (misal Beginner vs Advanced), beri penalti ekstra
    if (diff > 2) {
      return Math.pow(diff - 2, 2) * 5000; // Penalti eksponensial untuk perbedaan besar
    }
    return 0;
  };
  
  penalty += levelDifferencePenalty(teamA, teamB);
  
  // ATURAN BARU: Larang keras jika ada Beginner di grup dan lawannya semua Advanced
  const totalBeginnerCount = group.filter(p => isBeginner(p.level)).length;
  const totalAdvancedCount = group.filter(p => isAdvanced(p.level)).length;
  if (totalBeginnerCount > 0 && totalAdvancedCount >= 2) {
    // Jika ada Beginner dan minimal 2 Advanced, beri penalti ekstra
    if (groupHasIntermediate) {
      penalty += 8000; // Jika ada Intermediate, seharusnya bisa dihindari
    } else {
      penalty += 4000; // Jika tidak ada Intermediate, penalti lebih rendah
    }
  }
  
  if (aAdvCnt === 2 && bBegCnt2 >= 1) penalty += 5000;
  if (bAdvCnt === 2 && aBegCnt2 >= 1) penalty += 5000;
  if (aBegCnt2 === 2 && bAdvCnt >= 1) penalty += 5000;
  if (bBegCnt2 === 2 && aAdvCnt >= 1) penalty += 5000;
  if (aBegCnt2 === 2 && bBegCnt2 === 0 && bIntCnt === 0) penalty += 3000;
  if (bBegCnt2 === 2 && aBegCnt2 === 0 && aIntCnt === 0) penalty += 3000;

  // DISTRIBUSI GANDONGAN: Advanced menggandong Beginner/Intermediate
  if (gandongCount) {
    const pairA = getAdvCarryPair(teamA);
    const pairB = getAdvCarryPair(teamB);
    if (pairA.advanced && pairA.carried) {
      const times = gandongCount[pairA.advanced.name] || 0;
      penalty += times > 0 ? 1200 + times * 400 : 0;
    }
    if (pairB.advanced && pairB.carried) {
      const times = gandongCount[pairB.advanced.name] || 0;
      penalty += times > 0 ? 1200 + times * 400 : 0;
    }
  }

  // PREFERENSI LEVEL + DENGAN - SEBAGAI PARTNER, LAWAN NETRAL
  // Bonus untuk kombinasi + dengan - sebagai partner, dan lawannya netral
  const hasPlusLevel = (team: PlayerInput[]) => team.some(p => p.level.endsWith('+'));
  const hasMinusLevel = (team: PlayerInput[]) => team.some(p => p.level.endsWith('-'));
  
  const teamAPlusMinus = hasPlusLevel(teamA) && hasMinusLevel(teamA);
  const teamBPlusMinus = hasPlusLevel(teamB) && hasMinusLevel(teamB);
  const teamANeutral = teamA.every(p => !p.level.endsWith('+') && !p.level.endsWith('-') && p.level !== 'Beginner');
  const teamBNeutral = teamB.every(p => !p.level.endsWith('+') && !p.level.endsWith('-') && p.level !== 'Beginner');
  
  // Bonus untuk + dengan - vs netral dengan netral
  if ((teamAPlusMinus && teamBNeutral) || (teamBPlusMinus && teamANeutral)) {
    penalty -= 1500; // Bonus besar untuk kombinasi ideal ini
  }
  
  // Bonus untuk + dengan - sebagai partner (meskipun lawannya tidak netral)
  if (teamAPlusMinus || teamBPlusMinus) {
    penalty -= 800; // Bonus sedang untuk + dengan - sebagai partner
  }

  // BONUS UNTUK KOMBINASI SEIMBANG ANTAR TIM
  // Bonus untuk kombinasi yang seimbang antara tim (bukan dalam tim)
  const teamAHasIntMinus = teamA.some(p => p.level === 'Intermediate -');
  const teamBHasIntMinus = teamB.some(p => p.level === 'Intermediate -');
  
  // Bonus untuk Int-/Adv vs Adv/Int- (kombinasi seimbang)
  if ((teamAHasIntMinus && teamAHasAdv && teamBHasIntMinus && teamBHasAdv) ||
      (teamBHasIntMinus && teamBHasAdv && teamAHasIntMinus && teamAHasAdv)) {
    penalty -= 1200; // Bonus besar untuk kombinasi seimbang ini
  }
  
  // Bonus untuk Int-/Adv vs Adv-/Int+ atau Int netral (masih seimbang)
  const teamAHasIntNeutral = teamA.some(p => p.level === 'Intermediate');
  const teamBHasIntNeutral = teamB.some(p => p.level === 'Intermediate');
  
  if ((teamAHasIntMinus && teamAHasAdv && 
       ((teamBHasAdvMinus && teamBHasIntPlus) || (teamBHasAdvMinus && teamBHasIntNeutral))) ||
      (teamBHasIntMinus && teamBHasAdv && 
       ((teamAHasAdvMinus && teamAHasIntPlus) || (teamAHasAdvMinus && teamAHasIntNeutral)))) {
    penalty -= 800; // Bonus sedang untuk kombinasi yang masih seimbang
  }

  // HINDARI PARTNER YANG SAMA: Beri penalti jika pemain pernah bermain dengan partner yang sama
  if (partnerHistory) {
    // Cek partner dalam teamA
    const teamAPartner1 = teamA[0].name;
    const teamAPartner2 = teamA[1].name;
    if (partnerHistory[teamAPartner1]?.has(teamAPartner2)) {
      penalty += 2000; // Penalti tinggi untuk partner yang sama
    }
    if (partnerHistory[teamAPartner2]?.has(teamAPartner1)) {
      penalty += 2000;
    }
    
    // Cek partner dalam teamB
    const teamBPartner1 = teamB[0].name;
    const teamBPartner2 = teamB[1].name;
    if (partnerHistory[teamBPartner1]?.has(teamBPartner2)) {
      penalty += 2000;
    }
    if (partnerHistory[teamBPartner2]?.has(teamBPartner1)) {
      penalty += 2000;
    }
  }

  // HINDARI LAWAN YANG SAMA: Beri penalti jika pemain pernah bermain melawan lawan yang sama
  if (opponentHistory) {
    const teamAPartner1 = teamA[0].name;
    const teamAPartner2 = teamA[1].name;
    const teamBPartner1 = teamB[0].name;
    const teamBPartner2 = teamB[1].name;
    
    // Cek lawan dari teamA ke teamB
    if (opponentHistory[teamAPartner1]?.has(teamBPartner1) || opponentHistory[teamAPartner1]?.has(teamBPartner2)) {
      penalty += 3000; // Penalti sangat tinggi untuk lawan yang sama
    }
    if (opponentHistory[teamAPartner2]?.has(teamBPartner1) || opponentHistory[teamAPartner2]?.has(teamBPartner2)) {
      penalty += 3000;
    }
    
    // Cek lawan dari teamB ke teamA
    if (opponentHistory[teamBPartner1]?.has(teamAPartner1) || opponentHistory[teamBPartner1]?.has(teamAPartner2)) {
      penalty += 3000;
    }
    if (opponentHistory[teamBPartner2]?.has(teamAPartner1) || opponentHistory[teamBPartner2]?.has(teamAPartner2)) {
      penalty += 3000;
    }
  }

  if (
    ((teamA.some(p => p.level === 'Intermediate') && teamA.some(p => p.level === 'Advanced -')) &&
      (teamB.some(p => p.level === 'Intermediate') && teamB.some(p => p.level === 'Beginner')))
    ||
    ((teamB.some(p => p.level === 'Intermediate') && teamB.some(p => p.level === 'Advanced -')) &&
      (teamA.some(p => p.level === 'Intermediate') && teamA.some(p => p.level === 'Beginner')))
  ) {
    // Penalti sangat tinggi karena ini jenis pairing yang sangat berat sebelah di dunia nyata
    penalty += 20000;
  }

  // [RULE] Penalti sangat tinggi untuk Intermediate + Advanced apapun vs Intermediate + Beginner, agar benar-benar terhindarkan
  const teamAIntAndAdv = teamA.some(p => p.level === 'Intermediate') && teamA.some(p => isAdvanced(p.level));
  const teamBIntAndBegin = teamB.some(p => p.level === 'Intermediate') && teamB.some(p => isBeginner(p.level));
  const teamBIntAndAdv = teamB.some(p => p.level === 'Intermediate') && teamB.some(p => isAdvanced(p.level));
  const teamAIntAndBegin = teamA.some(p => p.level === 'Intermediate') && teamA.some(p => isBeginner(p.level));
  if ((teamAIntAndAdv && teamBIntAndBegin) || (teamBIntAndAdv && teamAIntAndBegin)) {
    penalty += 20000; // Penalti sangat tinggi karena masif mismatch di dunia nyata
  }

  return penalty;
}

// Picker grup 4 pemain: prioritaskan Advanced, kemudian Intermediate, baru Beginner
function pickGroupOfFour(pool: PlayerInput[], lastPlayed: Set<string>, playCount: Record<string, number>): PlayerInput[] | null {
  if (pool.length < 4) return null;

  // ------------- PRIORITAS ADVANCED GROUP SENDIRI ------------------
  const advanced = pool.filter(p => isAdvanced(p.level));
  if (advanced.length >= 4) {
    // Jika minimal ada 4 Advanced, selalu group-kan mereka dulu
    return advanced.slice(0, 4);
  }
  // Jika ada 2 atau 3 Advanced, prioritaskan mereka dalam satu grup
  if (advanced.length >= 2) {
    // PRIORITAS MUTLAK: Hanya ambil Advanced atau Intermediate tertinggi untuk melengkapi grup Advanced
    // JANGAN ambil Beginner sama sekali
    const intermediateForAdvanced = pool.filter(p => isIntermediate(p.level)).sort((a, b) => getLevelIndex(b.level) - getLevelIndex(a.level));
    if (intermediateForAdvanced.length >= (4 - advanced.length)) {
      const groupAdvanced = [...advanced, ...intermediateForAdvanced.slice(0, 4 - advanced.length)];
      if (groupAdvanced.length === 4) return groupAdvanced;
    }
  }
  // Jika hanya ada 1 Advanced, tunggu sampai ada Advanced lain atau gunakan Intermediate tertinggi
  if (advanced.length === 1) {
    // Cek apakah masih ada Advanced lain di pool yang belum dipilih
    // Jika tidak ada, gunakan Intermediate tertinggi (Intermediate + atau Intermediate)
    const intermediateForAdvanced = pool.filter(p => isIntermediate(p.level)).sort((a, b) => getLevelIndex(b.level) - getLevelIndex(a.level));
    if (intermediateForAdvanced.length >= 3) {
      const groupAdvanced = [...advanced, ...intermediateForAdvanced.slice(0, 3)];
      if (groupAdvanced.length === 4) return groupAdvanced;
    }
  }
  // ------------- END PRIORITAS ADVANCED GROUP SENDIRI --------------

  // ------------- PRIORITAS INTERMEDIATE GROUP SENDIRI ------------------
  const intermediate = pool.filter(p => isIntermediate(p.level));
  if (intermediate.length >= 4) {
    // Jika minimal ada 4 Intermediate, selalu group-kan mereka dulu
    return intermediate.slice(0, 4);
  }
  // Jika tersisa 2 atau 3 Intermediate, coba prioritaskan mereka dalam satu grup (campuran jika kurang dari 4)
  if (intermediate.length >= 2) {
    // Cari 2-3 Intermediate lalu tambah beginner jika perlu untuk menambah kuota
    const othersIntermediate = pool.filter(p => !isIntermediate(p.level)).sort((a, b) => getLevelIndex(b.level) - getLevelIndex(a.level));
    const groupIntermediate = [...intermediate, ...othersIntermediate.slice(0, 4 - intermediate.length)];
    if (groupIntermediate.length === 4) return groupIntermediate;
  }
  // ------------- END PRIORITAS INTERMEDIATE GROUP SENDIRI --------------

  // Kandidat disortir: bukan lastPlayed dulu, Advanced dulu, jumlah main lebih sedikit dulu
  const candidates = [...pool].sort((p1, p2) => {
    const lp1 = lastPlayed.has(p1.name) ? 1 : 0;
    const lp2 = lastPlayed.has(p2.name) ? 1 : 0;
    if (lp1 !== lp2) return lp1 - lp2;
    // Prioritaskan Advanced (0), kemudian Intermediate (1), baru Beginner (2)
    const p1Priority = getLevelIndex(p1.level) >= 4 ? 0 : (getLevelIndex(p1.level) === 0 ? 2 : 1);
    const p2Priority = getLevelIndex(p2.level) >= 4 ? 0 : (getLevelIndex(p2.level) === 0 ? 2 : 1);
    if (p1Priority !== p2Priority) return p1Priority - p2Priority;
    const c1 = playCount[p1.name] || 0;
    const c2 = playCount[p2.name] || 0;
    return c1 - c2;
  });
  const advCandidates = candidates.filter(c => isAdvanced(c.level));
  const intermediateCandidates = candidates.filter(c => isIntermediate(c.level));
  const beginnerCandidates = candidates.filter(c => isBeginner(c.level));
  
  // Ambil sesuai prioritas: Advanced dulu, lalu Intermediate, baru Beginner
  // ATURAN KETAT: Jangan campur Advanced dengan Beginner jika masih ada Intermediate
  const group: PlayerInput[] = [];
  const hasAdvancedInCandidates = advCandidates.length > 0;
  
  // Jika ada Advanced, prioritaskan Advanced dulu
  for (const p of advCandidates) {
    if (group.length < 4) group.push(p);
  }
  
  // Jika sudah ada Advanced di grup, JANGAN tambahkan Beginner jika masih ada Intermediate
  if (hasAdvancedInCandidates && group.length > 0) {
    // Hanya tambahkan Intermediate, bukan Beginner
    for (const p of intermediateCandidates) {
      if (group.length < 4) group.push(p);
    }
  } else {
    // Jika tidak ada Advanced, bisa tambahkan Intermediate dan Beginner
    for (const p of intermediateCandidates) {
      if (group.length < 4) group.push(p);
    }
    for (const p of beginnerCandidates) {
      if (group.length < 4) group.push(p);
    }
  }
  
  // CEK: Hindari grup yang berisi Advanced/Intermediate+ dan Beginner jika masih ada Intermediate lain di pool
  if (group.length === 4) {
    const groupHasAdvanced = group.some(p => isAdvanced(p.level));
    const groupHasIntermediatePlus = group.some(p => p.level === 'Intermediate +');
    const groupHasBeginner = group.some(p => isBeginner(p.level));
    const groupBeginnerCount = group.filter(p => isBeginner(p.level)).length;
    
    // Jika grup berisi Advanced/Intermediate+ dan Beginner, cek apakah masih ada Intermediate lain di pool
    if ((groupHasAdvanced || groupHasIntermediatePlus) && groupHasBeginner) {
      const remainingIntermediate = pool.filter(p => 
        !group.includes(p) && isIntermediate(p.level)
      );
      
      // Jika masih ada Intermediate yang tersedia, coba buat grup yang lebih seimbang
      if (remainingIntermediate.length >= groupBeginnerCount) {
        // Coba buat grup baru tanpa Beginner jika memungkinkan
        const newGroup: PlayerInput[] = [];
        // Ambil Advanced dulu
        for (const p of advCandidates) {
          if (newGroup.length < 4) newGroup.push(p);
        }
        // Ambil Intermediate (termasuk yang tersisa di pool)
        for (const p of intermediateCandidates) {
          if (newGroup.length < 4) newGroup.push(p);
        }
        // Tambahkan Intermediate yang tersisa dari pool jika masih kurang
        if (newGroup.length < 4) {
          for (const p of remainingIntermediate) {
            if (newGroup.length < 4 && !newGroup.includes(p)) newGroup.push(p);
          }
        }
        // Hanya tambahkan Beginner jika benar-benar tidak ada alternatif
        if (newGroup.length < 4) {
          for (const p of beginnerCandidates) {
            if (newGroup.length < 4) newGroup.push(p);
          }
        }
        if (newGroup.length === 4) return newGroup;
      }
    }
  }
  
  return group.length === 4 ? group : null;
}

function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

async function sleep(ms: number) { return new Promise(res => setTimeout(res, ms)); }

// Versi async incremental untuk mencegah freeze UI
async function multiMatchPairingAsync(players: PlayerInput[], minGames = 3): Promise<MultiMatchResult> {
  // Prioritaskan Advanced dulu, kemudian Intermediate, baru Beginner
  let playerList = [...players].sort((a, b) => {
    // Advanced dulu (index 4+), kemudian Intermediate (1-3), baru Beginner (0)
    const aIsAdvanced = getLevelIndex(a.level) >= 4 ? 0 : (getLevelIndex(a.level) === 0 ? 2 : 1);
    const bIsAdvanced = getLevelIndex(b.level) >= 4 ? 0 : (getLevelIndex(b.level) === 0 ? 2 : 1);
    if (aIsAdvanced !== bIsAdvanced) return aIsAdvanced - bIsAdvanced;
    // Jika dalam kategori yang sama, sortir berdasarkan level index (tertinggi lebih dulu)
    return getLevelIndex(b.level) - getLevelIndex(a.level);
  });
  const matchSize = 4;
  const playCount: Record<string, number> = {};
  playerList.forEach(p => (playCount[p.name] = 0));
  const matches: PairingResult[][] = [];
  let lastPlayed: Set<string> = new Set();
  const playerMatches: Record<string, number[]> = {};
  playerList.forEach(p => (playerMatches[p.name] = []));
  const gandongCount: Record<string, number> = {};
  const gandongDetails: Array<{ advanced: string; carried: string }> = [];
  // Track partner history untuk memastikan partner berbeda setiap match
  const partnerHistory: Record<string, Set<string>> = {};
  playerList.forEach(p => (partnerHistory[p.name] = new Set()));
  
  // Track lawan history untuk memastikan lawan berbeda setiap match
  const opponentHistory: Record<string, Set<string>> = {};
  playerList.forEach(p => (opponentHistory[p.name] = new Set()));

  let processedBatches = 0;

  for (let round = 0; round < minGames; round++) {
    let pool = playerList.filter(p => playCount[p.name] <= round);
    if (pool.length % matchSize !== 0) {
      const need = matchSize - (pool.length % matchSize);
      const candidates = playerList
        .filter(p => !pool.includes(p))
        .sort((a, b) => playCount[a.name] - playCount[b.name]);
      for (let i = 0; i < need && i < candidates.length; i++) {
        pool.push(candidates[i]);
      }
    }
    // Sort pool dengan prioritas yang sama seperti awal: Advanced dulu, Intermediate, Beginner terakhir
    pool.sort((a, b) => {
      const aIsAdvanced = getLevelIndex(a.level) >= 4 ? 0 : (getLevelIndex(a.level) === 0 ? 2 : 1);
      const bIsAdvanced = getLevelIndex(b.level) >= 4 ? 0 : (getLevelIndex(b.level) === 0 ? 2 : 1);
      if (aIsAdvanced !== bIsAdvanced) return aIsAdvanced - bIsAdvanced;
      return getLevelIndex(b.level) - getLevelIndex(a.level);
    });
    // Shuffle pool setelah sort untuk membuat variasi grup setiap round
    pool = shuffleArray(pool);
    const roundMatches: PairingResult[] = [];

    let idx = 0;
    while (idx < pool.length) {
      const group = pickGroupOfFour(pool.slice(idx), lastPlayed, playCount) || pool.slice(idx, idx + 4);
      if (group.length < 4) break;
      const namesInGroup = new Set(group.map(g => g.name));
      const newPool: PlayerInput[] = [];
      let taken = 0;
      for (let j = idx; j < pool.length; j++) {
        if (taken < 4 && namesInGroup.has(pool[j].name)) { taken++; continue; }
        newPool.push(pool[j]);
      }
      pool = newPool;

      const allSameLevel = group.every(p => p.level === group[0].level);
      let pairing: PairingResult;
      if (allSameLevel) {
        pairing = { teamA: [group[0], group[1]], teamB: [group[2], group[3]], isGandongan: false };
      } else {
        let best: { penalty: number; diff: number; gandongan: number; pair: PairingResult } | null = null;
        const comb = allCombinations(group);
        for (let i = 0; i < comb.length; i++) {
          const teamA = comb[i];
          const teamB = group.filter(p => !teamA.includes(p));
          // BLOCK REMATCH DAN PARTNER SAMA DI ROUND BERURUTAN
          let shouldSkip = false;
          if (round > 0 && matches.length > 0) {
            const prevRound = matches[matches.length - 1];
            for (const prev of prevRound) {
              // blok partner sama persis (teamA) 
              const partnerA = teamA.map(p=>p.name).sort().join('-');
              const prevPartnerA = prev.teamA.map(p=>p.name).sort().join('-');
              if (partnerA === prevPartnerA) { shouldSkip = true; break; }
              // blok partner sama persis (teamB) 
              const partnerB = teamB.map(p=>p.name).sort().join('-');
              const prevPartnerB = prev.teamB.map(p=>p.name).sort().join('-');
              if (partnerB === prevPartnerB) { shouldSkip = true; break; }
              // blok rematch 4 pemain apapun susunan (bareng)
              const setNow = new Set([...teamA.map(p=>p.name), ...teamB.map(p=>p.name)]);
              const setPrev = new Set([...prev.teamA.map(p=>p.name), ...prev.teamB.map(p=>p.name)]);
              if (setNow.size === 4 && setPrev.size === 4) {
                let sama = true;
                for (const n of Array.from(setNow)) if (!setPrev.has(n)) sama = false;
                for (const n of Array.from(setPrev)) if (!setNow.has(n)) sama = false;
                if (sama) { shouldSkip = true; break; }
              }
            }
          }
          if (shouldSkip) continue; // Skip kombinasi ini, coba kombinasi berikutnya
          
          const sumA = getTeamLevelSum(teamA);
          const sumB = getTeamLevelSum(teamB);
          const diff = Math.abs(sumA - sumB);
          const gandongan = countGandongan(teamA) + countGandongan(teamB);
          let penalty = pairPenalty(teamA, teamB, group, gandongCount, partnerHistory, opponentHistory);

          // penalti ekstra jika combo partner/lawan sama dengan round sebelumnya (exact match)
          if (round > 0 && matches.length > 0) {
            const prevRound = matches[matches.length - 1];
            for (const prev of prevRound) {
              const prevA = prev.teamA.map(p=>p.name).sort().join('-');
              const prevB = prev.teamB.map(p=>p.name).sort().join('-');
              const currA = teamA.map(p=>p.name).sort().join('-');
              const currB = teamB.map(p=>p.name).sort().join('-');
              // Partner sama persis");
              if ((currA === prevA && currB === prevB) || (currA === prevB && currB === prevA)) {
                penalty += 10000;
              }
              // Lawan sama persis walau partner tukar posisi
              if (
                (currA === prevA && currB === prevB) ||
                (currA === prevB && currB === prevA)
              ) {
                penalty += 10000;
              }
            }
          }
          if (best === null ||
              penalty < best.penalty ||
              (penalty === best.penalty && (diff < best.diff || (diff === best.diff && gandongan < best.gandongan)))) {
            best = { penalty, diff, gandongan, pair: { teamA, teamB, isGandongan: gandongan > 0 } };
          }
        }
        // Jika semua kombinasi terblokir, gunakan kombinasi pertama sebagai fallback
        if (best === null) {
          pairing = { teamA: [group[0], group[1]], teamB: [group[2], group[3]], isGandongan: false };
        } else {
          pairing = best.pair;
        }
      }

      roundMatches.push(pairing);
      const groupAll = [...pairing.teamA, ...pairing.teamB];
      
      // Update partner history untuk menghindari partner yang sama di match berikutnya
      const teamAPartner1 = pairing.teamA[0].name;
      const teamAPartner2 = pairing.teamA[1].name;
      const teamBPartner1 = pairing.teamB[0].name;
      const teamBPartner2 = pairing.teamB[1].name;
      
      // Tambahkan partner ke history masing-masing pemain
      if (partnerHistory[teamAPartner1]) partnerHistory[teamAPartner1].add(teamAPartner2);
      if (partnerHistory[teamAPartner2]) partnerHistory[teamAPartner2].add(teamAPartner1);
      if (partnerHistory[teamBPartner1]) partnerHistory[teamBPartner1].add(teamBPartner2);
      if (partnerHistory[teamBPartner2]) partnerHistory[teamBPartner2].add(teamBPartner1);
      
      // Tambahkan lawan ke history masing-masing pemain
      if (opponentHistory[teamAPartner1]) {
        opponentHistory[teamAPartner1].add(teamBPartner1);
        opponentHistory[teamAPartner1].add(teamBPartner2);
      }
      if (opponentHistory[teamAPartner2]) {
        opponentHistory[teamAPartner2].add(teamBPartner1);
        opponentHistory[teamAPartner2].add(teamBPartner2);
      }
      if (opponentHistory[teamBPartner1]) {
        opponentHistory[teamBPartner1].add(teamAPartner1);
        opponentHistory[teamBPartner1].add(teamAPartner2);
      }
      if (opponentHistory[teamBPartner2]) {
        opponentHistory[teamBPartner2].add(teamAPartner1);
        opponentHistory[teamBPartner2].add(teamAPartner2);
      }
      
      for (const p of groupAll) {
        // Cap maksimal 3x main per pemain
        if ((playCount[p.name] || 0) < minGames) {
          playCount[p.name]++;
          playerMatches[p.name].push(matches.length + roundMatches.length - 1);
        }
      }
      const abA = getAdvCarryPair(pairing.teamA);
      if (abA.advanced && abA.carried) {
        gandongCount[abA.advanced.name] = (gandongCount[abA.advanced.name] || 0) + 1;
        gandongDetails.push({ advanced: abA.advanced.name, carried: abA.carried.name });
      }
      const abB = getAdvCarryPair(pairing.teamB);
      if (abB.advanced && abB.carried) {
        gandongCount[abB.advanced.name] = (gandongCount[abB.advanced.name] || 0) + 1;
        gandongDetails.push({ advanced: abB.advanced.name, carried: abB.carried.name });
      }

      processedBatches++;
      if (processedBatches % 5 === 0) {
        await sleep(0);
      }
    }

    matches.push(roundMatches);
    const currentLastPlayed = new Set<string>();
    roundMatches.forEach(match => {
      [...match.teamA, ...match.teamB].forEach(p => currentLastPlayed.add(p.name));
    });
    lastPlayed = currentLastPlayed;

    await sleep(0);
  }

  // Hapus proses top-up: maksimal 3x main per pemain sesuai permintaan

  return { matches, playerMatches, gandongCount, gandongDetails };
}

const Matchmaking: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mode = theme.palette.mode;
  const [players, setPlayers] = useState<PlayerInput[]>([
    { name: '', level: LEVELS[0] },
    { name: '', level: LEVELS[0] },
    { name: '', level: LEVELS[0] },
    { name: '', level: LEVELS[0] },
  ]);
  const [waiting, setWaiting] = useState<PlayerInput[]>([]);
  const [error, setError] = useState('');
  const [lastShuffled, setLastShuffled] = useState<PlayerInput[]>([]);
  const [multiResult, setMultiResult] = useState<MultiMatchResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);

  // Tentukan apakah pengguna saat ini adalah superadmin (untuk akses drag & drop)
  const currentUser = (typeof window !== 'undefined') ? JSON.parse(localStorage.getItem('user') || '{}') : {};
  const isSuperAdmin = currentUser?.email ? isSuperAdminEmail(currentUser.email) : false;

  // Undo stack untuk menyimpan riwayat perubahan (bisa melakukan undo beberapa langkah)
  const [undoStack, setUndoStack] = useState<MultiMatchResult[]>([]);
  // Hover/target info saat drag (bisa target match atau pemain)
  const [dragOverTarget, setDragOverTarget] = useState<{
    type: 'match' | 'player';
    round: number;
    matchIdx: number;
    team?: 'A' | 'B';
    playerIdx?: number;
  } | null>(null);
  // Pesan aksi terakhir (sukses) untuk memberi feedback singkat
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  // Timestamp kapan terakhir tersimpan ke local
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Simpan multiResult ke localStorage
  const saveMultiResultLocally = (res: MultiMatchResult | null) => {
    if (typeof window === 'undefined') return;
    if (!res) {
      localStorage.removeItem('matchmaking_saved');
      localStorage.removeItem('matchmaking_saved_ts');
      setSavedAt(null);
      return;
    }
    try {
      localStorage.setItem('matchmaking_saved', JSON.stringify(res));
      const ts = Date.now();
      localStorage.setItem('matchmaking_saved_ts', String(ts));
      setSavedAt(ts);
    } catch (err) {
      console.error('Error saving matchmaking locally:', err);
    }
  };

  // Load saved multiResult from localStorage on mount
  React.useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const raw = localStorage.getItem('matchmaking_saved');
      const ts = localStorage.getItem('matchmaking_saved_ts');
      if (raw) {
        const parsed = JSON.parse(raw);
        setMultiResult(parsed);
      }
      if (ts) setSavedAt(Number(ts));
    } catch (err) {
      console.error('Error loading saved matchmaking:', err);
    }
  }, []);

  // Fetch sponsors
  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get<Sponsor[]>('/api/bookings?action=sponsor');
        setSponsors(response.data);
      } catch (error) {
        console.error('Error fetching sponsors:', error);
        setSponsors([]);
      }
    };

    fetchSponsors();
  }, []);

  // Monitor multiResult changes
  React.useEffect(() => {
    if (multiResult) {
      console.log('🔄 MULTIRESULT UPDATED:', multiResult);
      console.log('Matches structure:', multiResult.matches.map((round, rIdx) => 
        round.map((m, mIdx) => ({
          round: rIdx,
          match: mIdx,
          teamA: m.teamA.map(p => p.name),
          teamB: m.teamB.map(p => p.name)
        }))
      ));
    }
  }, [multiResult]);


  // Drag & drop handlers (hanya aktif untuk superadmin)
  const handleMatchDragStart = (e: React.DragEvent<HTMLDivElement>, fromRound: number, fromMatchIdx: number) => {
    if (!isSuperAdmin) return;
    console.log('🎯 MATCH DRAG START:', { fromRound, fromMatchIdx });
    const payload = JSON.stringify({ type: 'match', fromRound, fromMatchIdx });
    // set both application/json and text/plain for broader compatibility
    e.dataTransfer.setData('application/json', payload);
    e.dataTransfer.setData('text/plain', payload);
    e.dataTransfer.effectAllowed = 'move';
    setDragOverTarget(null);
  };

  const handlePlayerDragStart = (e: React.DragEvent<HTMLDivElement>, fromRound: number, fromMatchIdx: number, team: 'A' | 'B', playerIdx: number) => {
    if (!isSuperAdmin) return;
    e.stopPropagation(); // STOP event bubbling ke match container
    console.log('🎯 PLAYER DRAG START:', { fromRound, fromMatchIdx, team, playerIdx });
    const payload = JSON.stringify({ type: 'player', fromRound, fromMatchIdx, team, playerIdx });
    e.dataTransfer.setData('application/json', payload);
    e.dataTransfer.setData('text/plain', payload);
    e.dataTransfer.effectAllowed = 'move';
    setDragOverTarget(null);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, target: { type: 'match' | 'player'; round: number; matchIdx: number; team?: 'A' | 'B'; playerIdx?: number }) => {
    if (!isSuperAdmin) return;
    console.log('🟢 DRAG ENTER:', target);
    e.preventDefault();
    setDragOverTarget(target);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>, target: { type: 'match' | 'player'; round: number; matchIdx: number; team?: 'A' | 'B'; playerIdx?: number }) => {
    if (!isSuperAdmin) return;
    // Only clear if leaving same target
    setDragOverTarget(prev => (prev && prev.type === target.type && prev.round === target.round && prev.matchIdx === target.matchIdx && prev.team === target.team && prev.playerIdx === target.playerIdx ? null : prev));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Generic drop handler: supports match swap and player swap
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, toRound: number, toMatchIdx: number, toTeam?: 'A' | 'B', toPlayerIdx?: number) => {
    console.log('📥 HANDLE DROP CALLED:', { toRound, toMatchIdx, toTeam, toPlayerIdx });
    e.preventDefault();
    e.stopPropagation(); // STOP event bubbling ke match container
    if (!isSuperAdmin) {
      console.log('Not superadmin, returning');
      return;
    }
    setDragOverTarget(null);
    try {
      // Support both application/json and text/plain payloads
      let text = e.dataTransfer.getData('application/json');
      console.log('Payload (application/json):', text);
      if (!text) text = e.dataTransfer.getData('text/plain');
      console.log('Payload (text/plain):', text);
      if (!text) {
        console.debug('Drop ignored: no payload found');
        setActionMessage('Drop dibatalkan: payload tidak ditemukan');
        setTimeout(() => setActionMessage(null), 2000);
        return;
      }
      let payload: any;
      try {
        payload = JSON.parse(text);
      } catch (err) {
        console.error('Failed parsing drop payload', err);
        setActionMessage('Drop error: payload tidak valid');
        setTimeout(() => setActionMessage(null), 2000);
        return;
      }

      // quick UI debug so user can see what's being passed
      setActionMessage(`Drop payload: ${payload.type}${payload.team ? ` ${payload.team}${payload.playerIdx}` : ''} from R:${payload.fromRound + 1} M:${payload.fromMatchIdx + 1}`);
      setTimeout(() => setActionMessage(null), 1500);

      setMultiResult(prev => {
        if (!prev) return prev;
        const prevClone = JSON.parse(JSON.stringify(prev));
        setUndoStack(stack => [...stack, prevClone]);

        // Rebuild a fully new matches structure to avoid any hidden shared refs
        const newMatches = prev.matches.map((round, rIdx) => (
          round.map((match, mIdx) => ({
            teamA: match.teamA.map(p => ({ name: p.name, level: p.level })),
            teamB: match.teamB.map(p => ({ name: p.name, level: p.level })),
            isGandongan: match.isGandongan
          }))
        ));

        if (payload.type === 'match') {
          const { fromRound, fromMatchIdx } = payload;
          if (fromRound === undefined || fromMatchIdx === undefined) return prev;
          const temp = newMatches[fromRound][fromMatchIdx];
          newMatches[fromRound][fromMatchIdx] = newMatches[toRound][toMatchIdx];
          newMatches[toRound][toMatchIdx] = temp;

          setActionMessage(`Swap match ${fromMatchIdx + 1} ⇄ ${toMatchIdx + 1} (Round ${toRound + 1})`);
          setTimeout(() => setActionMessage(null), 2500);
        } else if (payload.type === 'player') {
          try {
            const { fromRound, fromMatchIdx, team: fromTeam, playerIdx: fromPlayerIdx } = payload;
            // Enforce same round only
            if (fromRound === undefined || fromMatchIdx === undefined || fromPlayerIdx === undefined) return prev;
            if (fromRound !== toRound) {
              // show error for a short moment
              setError('Pemindahan pemain hanya diperbolehkan di Round yang sama.');
              setTimeout(() => setError(''), 3000);
              return prev;
            }

            // If drop target did not specify a team/slot (dropped onto match container),
            // default to swap with the opposite team and same player index (or 0 if out of range)
            const targetTeam = toTeam ?? (fromTeam === 'A' ? 'B' : 'A');
            let targetPlayerIdx = typeof toPlayerIdx === 'number' ? toPlayerIdx : undefined;

            const srcMatch = newMatches[fromRound][fromMatchIdx];
            const dstMatch = newMatches[toRound][toMatchIdx];
            const srcTeamArr = fromTeam === 'A' ? srcMatch.teamA : srcMatch.teamB;
            const dstTeamArr = targetTeam === 'A' ? dstMatch.teamA : dstMatch.teamB;

            if (targetPlayerIdx === undefined) {
              // Use same index if possible, otherwise fallback to 0
              targetPlayerIdx = Math.min(fromPlayerIdx, dstTeamArr.length - 1);
              if (targetPlayerIdx < 0) targetPlayerIdx = 0;
            }

            // Debug logging
            console.log('=== DRAG & DROP PLAYER SWAP DEBUG ===');
            console.log('From:', { round: fromRound, match: fromMatchIdx, team: fromTeam, playerIdx: fromPlayerIdx, playerName: srcTeamArr[fromPlayerIdx]?.name });
            console.log('To:', { round: toRound, match: toMatchIdx, team: targetTeam, playerIdx: targetPlayerIdx, playerName: dstTeamArr[targetPlayerIdx]?.name });
            console.log('Before swap:');
            console.log('  Match', fromMatchIdx, ':', { teamA: srcMatch.teamA.map(p => p.name), teamB: srcMatch.teamB.map(p => p.name) });
            if (fromRound !== toRound || fromMatchIdx !== toMatchIdx) {
              console.log('  Match', toMatchIdx, ':', { teamA: dstMatch.teamA.map(p => p.name), teamB: dstMatch.teamB.map(p => p.name) });
            }

            // No-op if same exact slot
            if (fromRound === toRound && fromMatchIdx === toMatchIdx && fromTeam === targetTeam && fromPlayerIdx === targetPlayerIdx) {
              console.log('NO-OP: Same slot');
              setActionMessage('Tidak ada perubahan (sama).');
              setTimeout(() => setActionMessage(null), 1200);
              return prev;
            }

            // Swap by value (create new player objects and swap)
            const srcP = srcTeamArr[fromPlayerIdx];
            const dstP = dstTeamArr[targetPlayerIdx];

            const srcCopy = { name: srcP.name, level: srcP.level };
            const dstCopy = { name: dstP.name, level: dstP.level };

            console.log('Swapping:', srcCopy, '⇄', dstCopy);

            // Handle same match vs different match
            if (fromRound === toRound && fromMatchIdx === toMatchIdx) {
              console.log('SAME MATCH swap');
              // Same match: need to handle carefully since srcMatch === dstMatch
              const match = newMatches[fromRound][fromMatchIdx];
              
              // Create new arrays for both teams
              const newTeamA = [...match.teamA];
              const newTeamB = [...match.teamB];
              
              console.log('Before mutation:', { newTeamA: newTeamA.map(p => p.name), newTeamB: newTeamB.map(p => p.name) });
              
              // Apply swaps
              if (fromTeam === 'A' && targetTeam === 'A') {
                // Both in teamA (shouldn't happen given the no-op check, but handle it)
                console.log('Case: A→A');
                newTeamA[fromPlayerIdx] = dstCopy;
                newTeamA[targetPlayerIdx] = srcCopy;
              } else if (fromTeam === 'B' && targetTeam === 'B') {
                // Both in teamB (shouldn't happen given the no-op check, but handle it)
                console.log('Case: B→B');
                newTeamB[fromPlayerIdx] = dstCopy;
                newTeamB[targetPlayerIdx] = srcCopy;
              } else if (fromTeam === 'A' && targetTeam === 'B') {
                // A to B
                console.log('Case: A→B', { fromPlayerIdx, targetPlayerIdx });
                newTeamA[fromPlayerIdx] = dstCopy;
                newTeamB[targetPlayerIdx] = srcCopy;
              } else {
                // B to A
                console.log('Case: B→A', { fromPlayerIdx, targetPlayerIdx });
                newTeamB[fromPlayerIdx] = dstCopy;
                newTeamA[targetPlayerIdx] = srcCopy;
              }
              
              console.log('After mutation:', { newTeamA: newTeamA.map(p => p.name), newTeamB: newTeamB.map(p => p.name) });
              
              newMatches[fromRound][fromMatchIdx].teamA = newTeamA;
              newMatches[fromRound][fromMatchIdx].teamB = newTeamB;
              
              console.log('After assignment:', { 
                teamA: newMatches[fromRound][fromMatchIdx].teamA.map(p => p.name), 
                teamB: newMatches[fromRound][fromMatchIdx].teamB.map(p => p.name) 
              });
            } else {
              console.log('DIFFERENT MATCHES swap');
              // Different matches: handle separately
              // Create new array for source match's team
              const fromTeamArrNew = [...(fromTeam === 'A' ? srcMatch.teamA : srcMatch.teamB)];
              fromTeamArrNew[fromPlayerIdx] = dstCopy;
              if (fromTeam === 'A') {
                newMatches[fromRound][fromMatchIdx].teamA = fromTeamArrNew;
              } else {
                newMatches[fromRound][fromMatchIdx].teamB = fromTeamArrNew;
              }

              // Create new array for destination match's team
              const toTeamArrNew = [...(targetTeam === 'A' ? dstMatch.teamA : dstMatch.teamB)];
              toTeamArrNew[targetPlayerIdx] = srcCopy;
              if (targetTeam === 'A') {
                newMatches[toRound][toMatchIdx].teamA = toTeamArrNew;
              } else {
                newMatches[toRound][toMatchIdx].teamB = toTeamArrNew;
              }
            }

            console.log('Final newMatches[', fromRound, '][', fromMatchIdx, ']:', { 
              teamA: newMatches[fromRound][fromMatchIdx].teamA.map(p => p.name), 
              teamB: newMatches[fromRound][fromMatchIdx].teamB.map(p => p.name) 
            });
            console.log('=== END DEBUG ===');

            setActionMessage(`${srcCopy.name} ⇄ ${dstCopy.name}`);
            setTimeout(() => setActionMessage(null), 2500);
          } catch (innerErr) {
            console.error('Error during player swap', innerErr);
            setError('Error saat menukar pemain. Lihat konsol.');
            setTimeout(() => setError(''), 3000);
            return prev;
          }
        }

        const newRes = { ...prev, matches: newMatches };
        console.log('State update - multiResult:', newRes);
        console.log('Matches after update:', newRes.matches.map((round, rIdx) => 
          round.map((m, mIdx) => ({
            round: rIdx,
            match: mIdx,
            teamA: m.teamA.map(p => p.name),
            teamB: m.teamB.map(p => p.name)
          }))
        ));
        saveMultiResultLocally(newRes);
        return newRes;
      });
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    setUndoStack(stack => {
      const newStack = stack.slice(0, -1);
      const last = stack[stack.length - 1];
      setMultiResult(last);
      saveMultiResultLocally(last);
      return newStack;
    });
  };

  const handlePlayerChange = (idx: number, field: 'name' | 'level', value: string) => {
    const newPlayers = [...players];
    newPlayers[idx][field] = value;
    setPlayers(newPlayers);
  }; 

  const handlePasteNames = (idx: number, e: React.ClipboardEvent<any>) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length <= 1) return; // biarkan paste biasa jika hanya 1 baris
    e.preventDefault();
    const currentLevel = players[idx]?.level || LEVELS[0];
    const newPlayers = [...players];
    // Pastikan kapasitas cukup
    while (newPlayers.length < idx + lines.length) {
      newPlayers.push({ name: '', level: currentLevel });
    }
    // Isi nama berurutan mulai dari idx
    for (let i = 0; i < lines.length; i++) {
      newPlayers[idx + i] = {
        name: lines[i],
        level: newPlayers[idx + i]?.level ?? currentLevel
      };
    }
    setPlayers(newPlayers);
  };

  const handleAddPlayer = () => {
    setPlayers([...players, { name: '', level: LEVELS[0] }]);
  };

  const handleRemovePlayer = (idx: number) => {
    if (players.length <= 4) return;
    setPlayers(players.filter((_, i) => i !== idx));
  };

  const handlePairing = async (reshuffle = false) => {
    setError('');
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      let validPlayers = players.filter(p => p.name.trim());
      if (validPlayers.length < 4) {
        setError('Minimal 4 pemain untuk matchmaking.');
        setWaiting([]);
        setMultiResult(null);
        return;
      }
      if (reshuffle || lastShuffled.length === 0) {
        validPlayers = shuffleArray(validPlayers);
        setLastShuffled(validPlayers);
      } else {
        validPlayers = lastShuffled;
      }

      const temp = await multiMatchPairingAsync(validPlayers, 3);
      setMultiResult(temp);
      setUndoStack([]); // reset undo history when generating new pairing
      saveMultiResultLocally(temp);
      setWaiting([]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Komponen untuk menampilkan sponsor di sidebar
  const SponsorSidebar: React.FC<{ sponsors: Sponsor[]; position: 'left' | 'right' }> = ({ sponsors, position }) => {
    if (sponsors.length === 0) return null;
    
    return (
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-start',
          position: 'fixed',
          ...(position === 'left'
            ? { left: { xs: 0, lg: 'max(6px, calc((100vw - 1280px) / 2 - 120px))' } }
            : { right: { xs: 0, lg: 'max(6px, calc((100vw - 1280px) / 2 - 120px))' } }),
          top: 100,
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          width: { lg: 120, xl: 170 },
          zIndex: 100,
          px: { lg: 1, xl: 2 },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {sponsors.map((sponsor, index) => (
          <Box
            key={sponsor.id}
            component="img"
            src={sponsor.url}
            alt={`Sponsor ${index + 1}`}
            sx={{
              height: 'auto',
              width: '100%',
              maxWidth: { lg: 120, xl: 140 },
              objectFit: 'contain',
              boxShadow: mode === 'light'
                ? '0 8px 32px rgba(25,118,210,0.15)'
                : '0 8px 32px rgba(13,71,161,0.3)',
              borderRadius: 3,
              background: mode === 'light'
                ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
              backdropFilter: 'blur(10px)',
              border: mode === 'light'
                ? '0.5px solid rgba(255,255,255,0.4)'
                : '0.5px solid rgba(255,255,255,0.2)',
              p: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: mode === 'light'
                  ? '0 12px 40px rgba(25,118,210,0.2)'
                  : '0 12px 40px rgba(13,71,161,0.4)',
              }
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', py: 4, position: 'relative' }}>
      {/* Sponsor Sidebar Kiri - Desktop Only */}
      <SponsorSidebar sponsors={sponsors} position="left" />
      
      {/* Sponsor Sidebar Kanan - Desktop Only */}
      <SponsorSidebar sponsors={sponsors} position="right" />

      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h4" align="center" fontWeight={700} gutterBottom>
          Matchmaking Ganda Badminton
        </Typography>
      <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
        Masukkan nama dan level pemain. Sistem akan memasangkan partner dan lawan seadil mungkin berdasarkan level.
      </Typography>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2,
        background: (theme) => theme.palette.mode === 'dark' ? (isMobile ? '#0a1626' : 'linear-gradient(135deg, rgba(13,71,161,0.10) 0%, rgba(21,101,192,0.08) 100%)') : undefined,
        border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
      }}>
        <Grid container spacing={2}>
          {players.map((p, idx) => (
            <React.Fragment key={idx}>
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  label={`Nama Pemain ${idx + 1}`}
                  value={p.name}
                  onChange={e => handlePlayerChange(idx, 'name', e.target.value)}
                  onPaste={e => handlePasteNames(idx, e)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={10} sm={5} md={5}>
                <TextField
                  select
                  label="Level"
                  value={p.level}
                  onChange={e => handlePlayerChange(idx, 'level', e.target.value)}
                  fullWidth
                >
                  {LEVELS.map(l => (
                    <MenuItem key={l} value={l}>{l}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={2} sm={2} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {players.length > 4 && (
                  <Button color="error" onClick={() => handleRemovePlayer(idx)}>-</Button>
                )}
              </Grid>
            </React.Fragment>
          ))}
          <Grid item xs={12}>
            <Button variant="outlined" onClick={handleAddPlayer} sx={{ mt: 1 }}>+ Tambah Pemain</Button>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3, borderColor: (theme) => theme.palette.mode === 'dark' ? 'primary.main' : undefined }} />
        <Box textAlign="center">
          <Button variant="contained" color="primary" size="large" onClick={() => handlePairing(true)} disabled={isProcessing}>
            {isProcessing ? 'Memproses...' : 'Proses Matchmaking'}
          </Button>
        </Box>
        {isProcessing && (
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Typography color="text.secondary">Sedang menyusun jadwal, mohon tunggu...</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>
      {multiResult && multiResult.matches.length > 0 && (
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }} align="center">Jadwal Match</Typography>
          {multiResult.matches.map((round, roundIdx) => (
            <Box key={roundIdx} sx={{ mb: 3 }}>
              <Typography fontWeight={700} color="primary" sx={{ mb: 1 }}>
                Round {roundIdx + 1}
              </Typography> 
              <Grid container spacing={2}>
                {round.map((r, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Paper
                      draggable={isSuperAdmin}
                      onDragStart={(e) => handleMatchDragStart(e, roundIdx, idx)}
                      onDragEnter={(e) => handleDragEnter(e, { type: 'match', round: roundIdx, matchIdx: idx })}
                      onDragLeave={(e) => handleDragLeave(e, { type: 'match', round: roundIdx, matchIdx: idx })}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, roundIdx, idx)}
                      onDragEnd={() => setDragOverTarget(null)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: r.isGandongan ? '2px solid #ff9800' : '2px solid #1976d2',
                        background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : (r.isGandongan ? 'rgba(255, 152, 0, 0.08)' : 'rgba(25, 118, 210, 0.06)'),
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: isSuperAdmin ? 'grab' : 'default',
                        transition: 'box-shadow 150ms ease',
                        ...(dragOverTarget && dragOverTarget.type === 'match' && dragOverTarget.round === roundIdx && dragOverTarget.matchIdx === idx ? {
                          boxShadow: '0 8px 20px rgba(25,118,210,0.24)',
                          transform: 'scale(1.02)',
                          border: '3px dashed rgba(25,118,210,0.6)'
                        } : {})
                      }}
                    >
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-12deg)',
                        fontSize: { xs: '2.4rem', sm: '3.2rem', md: '4rem' },
                        fontWeight: 800,
                        color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                        letterSpacing: 2,
                        userSelect: 'none',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap'
                      }}>
                        RaketerS
                      </Box>
                      <Typography fontWeight={700} color={r.isGandongan ? 'warning.main' : 'primary.main'}>
                        Match #{idx + 1}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1, gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {r.teamA.map((p, pi) => (
                            <Paper
                              key={`a-${pi}`}
                              draggable={isSuperAdmin}
                              onDragStart={(e) => handlePlayerDragStart(e, roundIdx, idx, 'A', pi)}
                              onDragEnter={(e) => handleDragEnter(e, { type: 'player', round: roundIdx, matchIdx: idx, team: 'A', playerIdx: pi })}
                              onDragLeave={(e) => handleDragLeave(e, { type: 'player', round: roundIdx, matchIdx: idx, team: 'A', playerIdx: pi })}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, roundIdx, idx, 'A', pi)}
                              onDragEnd={() => setDragOverTarget(null)}
                              elevation={0}
                              sx={{
                                px: 1.2,
                                py: 0.6,
                                borderRadius: 1,
                                minWidth: 140,
                                textAlign: 'center',
                                cursor: isSuperAdmin ? 'grab' : 'default',
                                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                                ...(dragOverTarget && dragOverTarget.type === 'player' && dragOverTarget.round === roundIdx && dragOverTarget.matchIdx === idx && dragOverTarget.team === 'A' && dragOverTarget.playerIdx === pi ? {
                                  boxShadow: '0 6px 18px rgba(25,118,210,0.18)',
                                  transform: 'scale(1.03)',
                                  border: '2px dashed rgba(25,118,210,0.6)'
                                } : {})
                              }}
                            >
                              <Typography fontWeight={600}>
                                {`${p.name} (${p.level})`}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>

                        <Typography fontWeight={700} color="error" sx={{ mx: 1 }}>VS</Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {r.teamB.map((p, pi) => (
                            <Paper
                              key={`b-${pi}`}
                              draggable={isSuperAdmin}
                              onDragStart={(e) => handlePlayerDragStart(e, roundIdx, idx, 'B', pi)}
                              onDragEnter={(e) => handleDragEnter(e, { type: 'player', round: roundIdx, matchIdx: idx, team: 'B', playerIdx: pi })}
                              onDragLeave={(e) => handleDragLeave(e, { type: 'player', round: roundIdx, matchIdx: idx, team: 'B', playerIdx: pi })}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, roundIdx, idx, 'B', pi)}
                              onDragEnd={() => setDragOverTarget(null)}
                              elevation={0}
                              sx={{
                                px: 1.2,
                                py: 0.6,
                                borderRadius: 1,
                                minWidth: 140,
                                textAlign: 'center',
                                cursor: isSuperAdmin ? 'grab' : 'default',
                                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                                ...(dragOverTarget && dragOverTarget.type === 'player' && dragOverTarget.round === roundIdx && dragOverTarget.matchIdx === idx && dragOverTarget.team === 'B' && dragOverTarget.playerIdx === pi ? {
                                  boxShadow: '0 6px 18px rgba(25,118,210,0.18)',
                                  transform: 'scale(1.03)',
                                  border: '2px dashed rgba(25,118,210,0.6)'
                                } : {})
                              }}
                            >
                              <Typography fontWeight={600}>
                                {`${p.name} (${p.level})`}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ mt: 3, flexWrap: 'wrap' }}>
            <Button variant="outlined" color="secondary" onClick={() => handlePairing(true)} disabled={isProcessing}>
              {isProcessing ? 'Memproses...' : 'Pairing Ulang'}
            </Button>
            {isSuperAdmin && undoStack.length > 0 && (
              <Button variant="contained" color="secondary" onClick={handleUndo}>Undo</Button>
            )}
            {savedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Tersimpan lokal: {new Date(savedAt).toLocaleString()}
              </Typography>
            )}
          </Box> 
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Rekap Main per Pemain:</Typography>
            <ul>
              {Object.entries(multiResult.playerMatches).map(([nama, arr]) => (
                <li key={nama}>{nama}: {arr.length}x main (Match ke-{arr.map(i => i + 1).join(', ')})</li>
              ))}
            </ul>
          </Box>
          {actionMessage && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">{actionMessage}</Alert>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Ringkasan Gandongan:</Typography>
            {Object.keys(multiResult.gandongCount).length === 0 ? (
              <Typography color="text.secondary">Tidak ada gandongan tercatat.</Typography>
            ) : (
              <ul>
                {Object.entries(multiResult.gandongCount).map(([adv, count]) => (
                  <li key={adv}>{adv}: {count}x menggandong (Beginner/Intermediate)</li>
                ))}
              </ul>
            )}
            {multiResult.gandongDetails.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">Detail pasangan gandongan:</Typography>
                <ul>
                  {multiResult.gandongDetails.map((d, i) => (
                    <li key={i}>{d.advanced} menggandong {d.carried}</li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {waiting.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Alert severity="info">
            {waiting.length} pemain belum dapat dipasangkan: {waiting.map(p => `${p.name} (${p.level})`).join(', ')}
          </Alert>
        </Box>
      )}
      </Container>
    </Box>
  );
};

export default Matchmaking;
